import { AnalyticsUtils } from '@analytics/utils';
import { ErrorHandler, inject, Injectable } from '@angular/core';
import { openGenericModal } from '@innogy/common-ui-modals';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { filter, map, tap } from 'rxjs/operators';
import { JssPlatformService } from '@core/jss-platform';
import { LocationService } from '@core/jss-seo';
import { Router } from '@angular/router';

import {
  handleSolarPanelsFunnelAddressNotFoundErrorAction,
  handleSolarPanelsFunnelGenericErrorAction,
  handleSolarPanelsFunnelPrivateErrorAction,
  initializeSolarPanelsFunnelStepAction,
  mintSolarPanelsTrackingIdAction,
  navigateToHouseHasInsufficientRoofAreaPageAction,
  navigateToHouseIsMonumentPageAction,
  navigateToHouseIsUnsuitablePageAction,
  navigateToHouseNotFoundPageAction,
  navigateToOrderFormPageAction,
  navigateToProductOverviewPageAction,
  navigateToResultOverviewPageAction,
  navigateToSuitabilityCheckPageAction,
  onSolarPanelsFunnelNavigationAction,
  setSolarPanelsFunnelInitializedAction,
} from './solar-panels-funnel.actions';
import { selectSolarPanelsFunnelSettings } from './solar-panels-funnel.selectors';

const startPageKey = 'startPage';
const startingStep = 'wizard';

@Injectable()
export class SolarPanelsFunnelEffects {
  private readonly actions$ = inject(Actions);
  private readonly store$ = inject(Store);
  private readonly analyticsUtils = inject(AnalyticsUtils);
  private readonly jssPlatformService = inject(JssPlatformService);
  private readonly location = inject(LocationService);
  private readonly router = inject(Router);
  private readonly errorHandler = inject(ErrorHandler);

  funnelSettings$ = this.store$.select(selectSolarPanelsFunnelSettings);

  public readonly onInitSolarPanelsFunnel$ = createEffect(() =>
    this.actions$.pipe(
      ofType(initializeSolarPanelsFunnelStepAction),
      concatLatestFrom(() => [this.funnelSettings$]),
      filter(
        ([action, funnelSettings]) =>
          !this.jssPlatformService.isEditorActive() &&
          (!funnelSettings.initialized ||
            action.funnelSettings?.id !== funnelSettings.id)
      ),
      map(([{ step, funnelSettings }]) => {
        if (!funnelSettings) {
          return handleSolarPanelsFunnelPrivateErrorAction({
            message: `FunnelSettings are not defined on ${step}`,
          });
        } else if (!funnelSettings[startPageKey]) {
          return handleSolarPanelsFunnelPrivateErrorAction({
            message: `${String(startPageKey)} is not defined on FunnelSettings`,
          });
        } else if (step !== startingStep) {
          return onSolarPanelsFunnelNavigationAction({
            page: funnelSettings[startPageKey],
          });
        }

        return setSolarPanelsFunnelInitializedAction({ funnelSettings });
      })
    )
  );

  public readonly mintSolarPanelsFunnelTrackingId$ = createEffect(() =>
    this.actions$.pipe(
      ofType(setSolarPanelsFunnelInitializedAction),
      map(() =>
        mintSolarPanelsTrackingIdAction({
          id: this.analyticsUtils.generateAnalyticsE2EId(),
        })
      )
    )
  );

  public readonly onSolarPanelsFunnelGenericError$ = createEffect(() =>
    this.actions$.pipe(
      ofType(handleSolarPanelsFunnelGenericErrorAction),
      concatLatestFrom(() => this.funnelSettings$),
      map(([_, funnelSettings]) => {
        if (!funnelSettings.genericErrorModal) {
          return handleSolarPanelsFunnelPrivateErrorAction({
            message: 'genericErrorModal is not defined on FunnelSettings',
          });
        }
        return openGenericModal(funnelSettings.genericErrorModal);
      })
    )
  );

  public readonly onSolarPanelsFunnelNavigation$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(onSolarPanelsFunnelNavigationAction),
        tap(({ page }) => {
          const [currentPath] = this.router.url.split('?');
          if (page && currentPath !== page.href) {
            this.location.navigateScLink(page);
          }
        })
      ),
    { dispatch: false }
  );

  public readonly onSolarPanelsFunnelPrivateError$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(handleSolarPanelsFunnelPrivateErrorAction),
        tap(({ message }) => {
          this.errorHandler.handleError(new Error(message));
        })
      ),
    { dispatch: false }
  );

  // When fallout page found, go to page.
  public readonly displayAddressNotFoundPage$ = createEffect(() =>
    this.actions$.pipe(
      ofType(handleSolarPanelsFunnelAddressNotFoundErrorAction),
      concatLatestFrom(() => this.funnelSettings$),
      filter(([, { houseNotFoundPage }]) => !!houseNotFoundPage?.href),
      map(([_, { houseNotFoundPage }]) =>
        navigateToHouseNotFoundPageAction({
          pageData: houseNotFoundPage,
        })
      )
    )
  );

  // When no fallout page is found, try to open a modal
  public readonly displayAddressNotFoundErrorModal$ = createEffect(() =>
    this.actions$.pipe(
      ofType(handleSolarPanelsFunnelAddressNotFoundErrorAction),
      concatLatestFrom(() => this.funnelSettings$),
      filter(([, { houseNotFoundPage }]) => !houseNotFoundPage?.href),
      map(([_, funnelSettings]) => {
        if (!funnelSettings.addressNotFoundErrorModal) {
          return handleSolarPanelsFunnelPrivateErrorAction({
            message:
              'addressNotFoundErrorModal is not defined on FunnelSettings',
          });
        }
        return openGenericModal(funnelSettings.addressNotFoundErrorModal);
      })
    )
  );

  public readonly handlePageNavigation$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        navigateToSuitabilityCheckPageAction,
        navigateToProductOverviewPageAction,
        navigateToResultOverviewPageAction,
        navigateToOrderFormPageAction,
        navigateToHouseIsMonumentPageAction,
        navigateToHouseIsUnsuitablePageAction,
        navigateToHouseNotFoundPageAction,
        navigateToHouseHasInsufficientRoofAreaPageAction
      ),
      map(({ pageData: page, type }) => {
        if (page?.href) {
          return onSolarPanelsFunnelNavigationAction({
            page,
          });
        }
        return handleSolarPanelsFunnelPrivateErrorAction({
          message: `${type} - No page found for this routing action in funnel settings.`,
        });
      })
    )
  );
}
